import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Play, Pause, SkipForward, SkipBack, Volume2, Mic, Square } from 'lucide-react';

interface AudioContent {
  id: string;
  title: string;
  duration: number;
  category: 'lesson' | 'quiz' | 'summary';
}

const mockAudioContent: AudioContent[] = [
  {
    id: '1',
    title: 'Introduction to React Components',
    duration: 480,
    category: 'lesson'
  },
  {
    id: '2',
    title: 'Understanding Props and State',
    duration: 360,
    category: 'lesson'
  },
  {
    id: '3',
    title: 'Quick Quiz: Component Basics',
    duration: 120,
    category: 'quiz'
  }
];

export const AudioFirstLearning: React.FC = () => {
  const [currentTrack, setCurrentTrack] = useState<AudioContent | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState([75]);
  const [playbackSpeed, setPlaybackSpeed] = useState([1]);
  const [isRecording, setIsRecording] = useState(false);

  const playTrack = (track: AudioContent) => {
    setCurrentTrack(track);
    setCurrentTime(0);
    setIsPlaying(true);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const skipForward = () => {
    if (currentTrack) {
      setCurrentTime(prev => Math.min(prev + 30, currentTrack.duration));
    }
  };

  const skipBackward = () => {
    setCurrentTime(prev => Math.max(prev - 30, 0));
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = (): number => {
    if (!currentTrack) return 0;
    return (currentTime / currentTrack.duration) * 100;
  };

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'lesson': return 'bg-blue-100 text-blue-800';
      case 'quiz': return 'bg-purple-100 text-purple-800';
      case 'summary': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const toggleRecording = () => {
    setIsRecording(!isRecording);
  };

  // Simulate audio playback
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying && currentTrack) {
      interval = setInterval(() => {
        setCurrentTime(prev => {
          const newTime = prev + 1;
          if (newTime >= currentTrack.duration) {
            setIsPlaying(false);
            return 0;
          }
          return newTime;
        });
      }, 1000 / playbackSpeed[0]);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentTrack, playbackSpeed]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Audio-First Learning</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Track Player */}
        {currentTrack && (
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="text-center">
                  <h3 className="font-semibold mb-2">{currentTrack.title}</h3>
                  <Badge className={getCategoryColor(currentTrack.category)}>
                    {currentTrack.category}
                  </Badge>
                </div>

                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="w-full bg-muted h-2 rounded-full">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${getProgressPercentage()}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(currentTrack.duration)}</span>
                  </div>
                </div>

                {/* Audio Controls */}
                <div className="flex items-center justify-center gap-4">
                  <Button variant="outline" size="sm" onClick={skipBackward}>
                    <SkipBack className="h-4 w-4" />
                  </Button>
                  <Button onClick={togglePlayPause} size="lg">
                    {isPlaying ? (
                      <Pause className="h-6 w-6" />
                    ) : (
                      <Play className="h-6 w-6" />
                    )}
                  </Button>
                  <Button variant="outline" size="sm" onClick={skipForward}>
                    <SkipForward className="h-4 w-4" />
                  </Button>
                </div>

                {/* Volume and Speed Controls */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Volume2 className="h-4 w-4" />
                      <span className="text-sm">Volume: {volume[0]}%</span>
                    </div>
                    <Slider
                      value={volume}
                      onValueChange={setVolume}
                      max={100}
                      step={5}
                      className="w-full"
                      aria-label="Volume control"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">Speed: {playbackSpeed[0]}x</span>
                    </div>
                    <Slider
                      value={playbackSpeed}
                      onValueChange={setPlaybackSpeed}
                      min={0.5}
                      max={2}
                      step={0.25}
                      className="w-full"
                      aria-label="Playback speed"
                    />
                  </div>
                </div>

                {/* Voice Recording */}
                <div className="text-center">
                  <Button
                    variant={isRecording ? "destructive" : "outline"}
                    onClick={toggleRecording}
                    className="gap-2"
                  >
                    {isRecording ? (
                      <>
                        <Square className="h-4 w-4" />
                        Stop Recording
                      </>
                    ) : (
                      <>
                        <Mic className="h-4 w-4" />
                        Record Question
                      </>
                    )}
                  </Button>
                  {isRecording && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Recording... Ask your question clearly
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Content Library */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Audio Content Library</h3>
          <div className="space-y-3">
            {mockAudioContent.map((content) => (
              <Card key={content.id} className="cursor-pointer hover:bg-muted/50">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium">{content.title}</h4>
                        <Badge className={getCategoryColor(content.category)}>
                          {content.category}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Duration: {formatTime(content.duration)}
                      </p>
                    </div>
                    <Button
                      onClick={() => playTrack(content)}
                      size="sm"
                      variant={currentTrack?.id === content.id ? "default" : "outline"}
                    >
                      <Play className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};